import React from "react";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * LegacyOverviewCard — Sub-sprint D-β-β.
 *
 * Privacy-safe legacy memberships overview. Renders hashes only,
 * never raw user_ids or emails.
 */
export const LegacyOverviewCard = ({ dataTestId = "rl-ops-legacy-overview" }) => {
  const t = useRacketLabAdminT();
  const { data, loading, error } = useAdminApi(
    "/admin/racket-lab/_ops/legacy-memberships-overview",
    { params: { sample_limit: 50 } },
  );

  if (loading) return <div data-testid={`${dataTestId}-loading`} className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500">{t("ops.legacy.overview.loading")}</div>;
  if (error) return (
    <div data-testid={`${dataTestId}-error`} className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
      {t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}
    </div>
  );
  if (!data) return null;

  return (
    <div data-testid={`${dataTestId}-card`} className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4">
      <header className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-slate-800">{t("ops.legacy.overview.card_title")}</h2>
        <span className="text-[10px] text-slate-500">{t("ops.legacy.overview.generated_at_label")} <code>{data.generated_at}</code></span>
      </header>
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800" data-testid={`${dataTestId}-total`}>total_legacy: <code>{data.total_legacy ?? 0}</code></span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5">sample_size: <code>{data.sample_size ?? 0}</code></span>
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">raw_user_ids_returned: <code>{String(data.privacy?.raw_user_ids_returned)}</code></span>
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">raw_emails_returned: <code>{String(data.privacy?.raw_emails_returned)}</code></span>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-2 py-1">{t("ops.legacy.overview.columns.user_hash")}</th>
              <th className="px-2 py-1">{t("ops.legacy.overview.columns.status")}</th>
              <th className="px-2 py-1">{t("ops.legacy.overview.columns.joined_at")}</th>
              <th className="px-2 py-1">{t("ops.legacy.overview.columns.source")}</th>
              <th className="px-2 py-1">{t("ops.legacy.overview.columns.rl_promotion")}</th>
              <th className="px-2 py-1">{t("ops.legacy.overview.columns.role")}</th>
            </tr>
          </thead>
          <tbody>
            {(data.sample || []).map((row, i) => (
              <tr key={i} data-testid={`${dataTestId}-sample-row-${i}`} className="border-t border-slate-100">
                <td className="px-2 py-1 font-mono text-slate-700">{row.user_hash || "—"}</td>
                <td className="px-2 py-1">{row.status || "—"}</td>
                <td className="px-2 py-1 text-slate-500">{(row.joined_at || "").slice(0, 19)}</td>
                <td className="px-2 py-1">{row.source || "—"}</td>
                <td className="px-2 py-1">{String(row.rl_promotion_present)}</td>
                <td className="px-2 py-1 font-mono">{row.current_role || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LegacyOverviewCard;
